import {FC, ReactNode, useEffect, useState} from 'react'
import {useDispatch} from 'react-redux'
import {FormProvider, SubmitHandler, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import Button from 'jbc-front/components/Button'
import {Radio, RadioContainer} from 'jbc-front/components/presenters/form/Radio'
import {Input} from 'jbc-front/components/presenters/form/Input'
import {Checkbox} from 'jbc-front/components/presenters/form/Checkbox'
import {Select} from 'jbc-front/components/presenters/form/Select'
import {ErrorMessage} from 'jbc-front/components/presenters/form/ErrorMessage'
import {Note} from 'jbc-front/components/presenters/ui/Note'
import {Label} from 'components/forms/Label'
import {BoxDouble} from 'components/ui/BoxDouble'
import {Panel, Body, Head} from 'components/ui/Panel'
import {DateField} from 'components/reactHookForm/DateField'
import {FormErrors} from 'components/reactHookForm/FormErrors'
import {useMutation, useQuery} from 'hooks/graphql'
import {EmployeeName} from 'pages/withAuth/adminPage/procedures/parts/EmployeeName'
import {ServerValidationError, eachValidationErrorsForHookFrom} from 'hooks/graphql/error/ServerValidationError'
import {dateFormat, fullNameFormat} from 'libs/formatter'
import {asyncError} from 'store/actions/asyncError'
import {ProcedureContext, ReportFormProps} from 'reportForms/ReportForm'
import {isChild} from 'reportForms/common/isChild'
import {schema as r507Schema} from 'reportForms/R507Report'
import {
  APPLICATION_TMP_DATA_NAME,
  PAST_CHILDCARE_PROCEDURES_FOR_REFLECT,
  PastChildcareProceduresForReflectQueryResult,
  PastChildcareProceduresForReflectQueryVariables,
  PROCEDURE_CHILDCARE_INFO_FORM,
  ProcedureChildcareInfoFormQueryResult,
  ProcedureChildcareInfoFormQueryVariables,
  UPDATE_CHILDCARE_INFO_FORM_DATUM,
  UPDATE_CHILDCARE_INFO_FORM_TMP_DATA,
  UpdateChildcareInfoFormDatumQueryVariables,
  UpdateChildcareInfoFormTmpDataQueryVariables,
} from './query'
import {Schema, schema} from './schema'
import styles from './ChildcareInfoForm.scss'

const TITLE = '育休情報を入力してください'

// 選択 section の出し分け:
//   new       → 「養育する子の選択」（扶養 children radio + 従業員情報から反映）
//   from_past → 「過去の手続きを選択」（dropdown + 手続きから反映）
//   todo_edit → section なし（Todo「手続き情報編集」/ 承認画面で、既存値をそのまま編集・確認）
type ChildcareInfoMode = 'new' | 'from_past' | 'todo_edit'

const pastProcedureNote = (procedureType: string) => {
  switch (procedureType) {
    case 'start_childcare':
      return '過去に作成した「出生時育休」から「対象の子」「休業期間」の情報を反映することができます。'
    case 'extend_childcare':
    case 'finish_childcare':
      return '過去に作成した「育休の開始」から「対象の子」「休業期間」の情報を反映することができます。'
    default:
      return null
  }
}

const procedureTmpTypeFromContext = (procedureContext: ProcedureContext): 'draft' | 'employee_draft' =>
  procedureContext === 'admin' ? 'draft' : 'employee_draft'

const isLeaveOfAbsenceReflectableProcedure = (procedureType: string) =>
  ['start_childcare', 'parental_leave'].includes(procedureType)

export const ChildcareInfoForm: FC<ReportFormProps & {isTodoEdit?: boolean}> = ({
  procedureStatusId,
  submitText,
  afterSubmit,
  procedureContext,
  isConfirm,
  comments,
  renderReject,
  isTodoEdit = false,
}) => {
  const {data, loading} = useQuery<ProcedureChildcareInfoFormQueryResult, ProcedureChildcareInfoFormQueryVariables>(
    PROCEDURE_CHILDCARE_INFO_FORM,
    {
      variables: {procedureStatusId, procedureTmpType: procedureTmpTypeFromContext(procedureContext)},
      fetchPolicy: 'network-only',
    }
  )
  const procedureStatus = data?.client?.procedureStatus

  if (loading || !procedureStatus) {
    return null
  }

  // MethodSelect スキップ時（過去手続なし）は method 未設定 → 'new' 扱い
  const method = procedureStatus.childcareMethodSelect?.data?.method
  // 承認（confirm）では admin が draft tmp を持たず method を取得できないため、入力補助の選択 section を
  // 出すと申請済みデータを上書きできてしまう。todo_edit と同様に選択 section を出さない。
  const mode: ChildcareInfoMode = isTodoEdit || isConfirm ? 'todo_edit' : method === 'from_past' ? 'from_past' : 'new'

  return (
    <div>
      <div className="l-title-wrap">
        <h1 className="m-title-main">{TITLE}</h1>
        <EmployeeName employee={procedureStatus.employee} />
      </div>
      <Form
        procedureStatus={procedureStatus}
        mode={mode}
        submitText={submitText}
        afterSubmit={afterSubmit}
        procedureContext={procedureContext}
        isConfirm={isConfirm}
        comments={comments}
        renderReject={renderReject}
        useApplicationTmp={procedureContext === 'mypage'}
      />
    </div>
  )
}

const makeApplicationDefaultValues = (
  ps: ProcedureChildcareInfoFormQueryResult['client']['procedureStatus']
): Schema | undefined => {
  if (!ps.applicationChildcareInfo?.data) {
    return undefined
  }

  // 差戻し退避等で必須項目が欠けた tmp は validateSync が render 中に throw する（白画面を避ける）
  try {
    return schema.validateSync(ps.applicationChildcareInfo.data, {strict: true})
  } catch {
    return undefined
  }
}

// 旧雇保 application tmp（r509/r512_param_set）から共通項目だけを取り出す
interface LegacyEmploymentInsuranceValues {
  startedOn?: string
  birthDate?: string
  expectedBirthOn?: string
}

const readLegacyEmploymentInsuranceTmp = (data: unknown): LegacyEmploymentInsuranceValues => {
  if (!data || typeof data !== 'object') {
    return {}
  }
  const record = data as Record<string, unknown>
  const asString = (value: unknown) => (typeof value === 'string' && value ? value : undefined)
  return {
    startedOn: asString(record['startedOn']),
    birthDate: asString(record['birthDate']),
    expectedBirthOn: asString(record['expectedBirthOn']),
  }
}

// release 前に作成された在途手続き（employee_draft / applying）は procedure_childcare_datum も
// childcare_info tmp も持たず、共通項目が旧 application tmp に残っている。datum 不在時はそこから復元する
// - 社保あり: procedure_childcare（R507）tmp から子情報・開始日・終了予定日
// - 雇保: r509/r512_param_set tmp から出産予定日（社保 tmp に無いため）、雇保のみ手続きでは開始日・出産年月日も
const makeLegacyChildcareDefaultValues = (
  ps: ProcedureChildcareInfoFormQueryResult['client']['procedureStatus']
): Schema | undefined => {
  let socialInsurance: ReturnType<typeof r507Schema.validateSync> | undefined
  if (ps.applicationChildcare?.data) {
    try {
      socialInsurance = r507Schema.validateSync(ps.applicationChildcare.data, {strict: true})
    } catch {
      socialInsurance = undefined
    }
  }

  const employmentInsurance = readLegacyEmploymentInsuranceTmp(ps.applicationR509?.data ?? ps.applicationR512?.data)

  // どの旧 tmp も無ければ復元対象なし
  if (!socialInsurance && !ps.applicationR509 && !ps.applicationR512) {
    return undefined
  }

  const childcare = socialInsurance?.childcareDatum
  const child = socialInsurance?.childcareChildDatum

  return {
    childcareDatum: {
      employeeDependentId: childcare?.employeeDependentId,
      startedOn: childcare?.startedOn || employmentInsurance.startedOn || '',
      expectedFinishedOn: childcare?.expectedFinishedOn || '',
      expectedBirthOn: employmentInsurance.expectedBirthOn || '',
      addToLeaveOfAbsence: false,
    },
    childcareChildDatum: {
      firstName: child?.firstName,
      lastName: child?.lastName,
      firstNameKana: child?.firstNameKana,
      lastNameKana: child?.lastNameKana,
      birthDate: child?.birthDate || employmentInsurance.birthDate || '',
      relation: child?.relation || 'real',
      parentingStartedDate: child?.parentingStartedDate || '',
      addSupport: child?.addSupport || false,
    },
  }
}

const makeDefaultValues = (
  ps: ProcedureChildcareInfoFormQueryResult['client']['procedureStatus'],
  useApplicationTmp: boolean
): Schema => {
  if (useApplicationTmp) {
    const applicationValues = makeApplicationDefaultValues(ps)
    if (applicationValues) {
      return applicationValues
    }
  }

  const childcare = ps.procedureChildcareDatum
  const child = childcare?.procedureChildcareChildDatum

  // datum 未作成（release 前の在途手続き等）は旧 application tmp から復元
  if (!childcare) {
    const legacyValues = makeLegacyChildcareDefaultValues(ps)
    if (legacyValues) {
      return legacyValues
    }
  }

  return {
    childcareDatum: {
      employeeDependentId: childcare?.employeeDependent?.id,
      startedOn: childcare?.startedOn ? dateFormat(childcare.startedOn, 'YYYY/MM/DD') : '',
      expectedFinishedOn: childcare?.expectedFinishedOn ? dateFormat(childcare.expectedFinishedOn, 'YYYY/MM/DD') : '',
      expectedBirthOn: ps.childcareExpectedBirthOn ? dateFormat(ps.childcareExpectedBirthOn, 'YYYY/MM/DD') : '',
      addToLeaveOfAbsence: childcare?.addToLeaveOfAbsence || false,
    },
    childcareChildDatum: {
      firstName: child?.firstName,
      lastName: child?.lastName,
      firstNameKana: child?.firstNameKana,
      lastNameKana: child?.lastNameKana,
      birthDate: child?.birthDate ? dateFormat(child.birthDate, 'YYYY/MM/DD') : '',
      relation: child?.relation || 'real',
      parentingStartedDate: child?.parentingStartedDate ? dateFormat(child.parentingStartedDate, 'YYYY/MM/DD') : '',
      addSupport: child?.addSupport || false,
    },
  }
}

interface FormProps {
  procedureStatus: ProcedureChildcareInfoFormQueryResult['client']['procedureStatus']
  mode: ChildcareInfoMode
  submitText: string
  afterSubmit: ReportFormProps['afterSubmit']
  procedureContext: ProcedureContext
  isConfirm?: boolean
  comments?: ReactNode
  renderReject?: ReportFormProps['renderReject']
  useApplicationTmp: boolean
}

const Form: FC<FormProps> = ({
  procedureStatus,
  mode,
  submitText,
  afterSubmit,
  procedureContext,
  comments,
  renderReject,
  useApplicationTmp,
}) => {
  const dispatch = useDispatch()
  const isAdmin = procedureContext === 'admin'
  const canAddToLeaveOfAbsence = isAdmin && isLeaveOfAbsenceReflectableProcedure(procedureStatus.procedureType)
  const leaveOfAbsenceReflected = !!procedureStatus.procedureChildcareDatum?.leaveOfAbsenceReflected
  const methods = useForm<Schema>({
    defaultValues: makeDefaultValues(procedureStatus, useApplicationTmp),
    resolver: yupResolver(schema),
    mode: 'onTouched',
    shouldFocusError: false,
    context: {procedureType: procedureStatus.procedureType},
  })
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    setError,
    formState: {errors, isSubmitting},
  } = methods

  const childRelation = watch('childcareChildDatum.relation')
  const employeeDependentId = watch('childcareDatum.employeeDependentId')

  // mode='new': 「従業員情報から反映する」— 選択中の扶養 child の情報を子情報フォームへ転記
  const handleCopyFromEmployeeDependent = () => {
    const dependent = procedureStatus.employee.employeeDependents.find(({id}) => id === employeeDependentId)
    if (!dependent) {
      return
    }
    setValue('childcareChildDatum.lastName', dependent.lastName, {shouldDirty: true})
    setValue('childcareChildDatum.firstName', dependent.firstName, {shouldDirty: true})
    setValue('childcareChildDatum.lastNameKana', dependent.lastNameKana, {shouldDirty: true})
    setValue('childcareChildDatum.firstNameKana', dependent.firstNameKana, {shouldDirty: true})
    setValue('childcareChildDatum.birthDate', dependent.birthday ? dependent.birthday.replaceAll('-', '/') : '', {
      shouldDirty: true,
    })
  }

  const childDependents = procedureStatus.employee.employeeDependents.filter((dependent) =>
    isChild(dependent.relationName, dependent.relationReportType)
  )

  // mode='from_past': 過去手続きドロップダウン（procedureType が判明した Form 内で skip 制御）
  const {data: pastData} = useQuery<
    PastChildcareProceduresForReflectQueryResult,
    PastChildcareProceduresForReflectQueryVariables
  >(PAST_CHILDCARE_PROCEDURES_FOR_REFLECT, {
    variables: {procedureStatusId: procedureStatus.id, forProcedureType: procedureStatus.procedureType},
    skip: mode !== 'from_past',
    fetchPolicy: 'network-only',
  })
  const pastProcedures = pastData?.client?.procedureStatus?.employee.pastChildcareProcedureStatuses ?? []
  const note = pastProcedureNote(procedureStatus.procedureType)
  const pastOptions = pastProcedures.map((p) => {
    const started = p.procedureChildcareDatum?.startedOn
      ? dateFormat(p.procedureChildcareDatum.startedOn, 'YYYY/MM/DD')
      : ''
    const c = p.procedureChildcareDatum?.procedureChildcareChildDatum
    const childName = c?.lastName && c?.firstName ? fullNameFormat(c.lastName, c.firstName) : ''
    const label = childName ? `育休開始日 ${started}\u{3000}子: ${childName}` : `育休開始日 ${started}`
    return {value: p.id, label}
  })

  const [selectedPastId, setSelectedPastId] = useState<string | undefined>()
  // 初期選択は先頭（最直近）。バックエンドが date_on DESC で返すため先頭が最新。
  const currentPastId = selectedPastId ?? pastProcedures[0]?.id

  // mode='from_past': 「手続きから情報を反映する」— 共有項目のみ転記
  const handleReflectFromPast = () => {
    const datum = pastProcedures.find((p) => p.id === currentPastId)?.procedureChildcareDatum
    if (!datum) {
      return
    }
    const child = datum.procedureChildcareChildDatum
    const opt = {shouldDirty: true}
    setValue('childcareChildDatum.lastName', child?.lastName ?? '', opt)
    setValue('childcareChildDatum.firstName', child?.firstName ?? '', opt)
    setValue('childcareChildDatum.lastNameKana', child?.lastNameKana ?? '', opt)
    setValue('childcareChildDatum.firstNameKana', child?.firstNameKana ?? '', opt)
    setValue('childcareChildDatum.birthDate', child?.birthDate ? dateFormat(child.birthDate, 'YYYY/MM/DD') : '', opt)
    setValue('childcareChildDatum.relation', child?.relation ?? 'real', opt)
    setValue(
      'childcareChildDatum.parentingStartedDate',
      child?.parentingStartedDate ? dateFormat(child.parentingStartedDate, 'YYYY/MM/DD') : '',
      opt
    )
    setValue('childcareDatum.startedOn', datum.startedOn ? dateFormat(datum.startedOn, 'YYYY/MM/DD') : '', opt)
    setValue(
      'childcareDatum.expectedFinishedOn',
      datum.expectedFinishedOn ? dateFormat(datum.expectedFinishedOn, 'YYYY/MM/DD') : '',
      opt
    )
    setValue(
      'childcareDatum.expectedBirthOn',
      datum.expectedBirthOn ? dateFormat(datum.expectedBirthOn, 'YYYY/MM/DD') : '',
      opt
    )
  }

  // 区分=実子に戻したら養育開始年月日をクリア
  useEffect(() => {
    if (childRelation !== 'other') {
      setValue('childcareChildDatum.parentingStartedDate', '', {
        shouldTouch: true,
        shouldDirty: true,
        shouldValidate: true,
      })
    }
  }, [childRelation, setValue])

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const blankToNull = (value: string | undefined | null) => (value ? value : null)

    try {
      await updateChildcareInfoFormDatum({
        variables: {
          input: {
            procedureStatusId: procedureStatus.id,
            attributes: {
              employeeDependentId: data.childcareDatum.employeeDependentId,
              startedOn: data.childcareDatum.startedOn,
              expectedBirthOn: blankToNull(data.childcareDatum.expectedBirthOn),
              expectedFinishedOn: blankToNull(data.childcareDatum.expectedFinishedOn),
              // addToLeaveOfAbsence は対象手続きの管理者のみ送信（member が送ると FORBIDDEN）
              ...(canAddToLeaveOfAbsence ? {addToLeaveOfAbsence: !!data.childcareDatum.addToLeaveOfAbsence} : {}),
              procedureChildcareChildDatum: {
                firstName: data.childcareChildDatum.firstName,
                lastName: data.childcareChildDatum.lastName,
                firstNameKana: data.childcareChildDatum.firstNameKana,
                lastNameKana: data.childcareChildDatum.lastNameKana,
                birthDate: data.childcareChildDatum.birthDate,
                relation: data.childcareChildDatum.relation,
                parentingStartedDate: blankToNull(data.childcareChildDatum.parentingStartedDate),
                addSupport: !!data.childcareChildDatum.addSupport,
              },
            },
          },
        },
      })
      if (!isAdmin) {
        await updateChildcareInfoFormTmpData({
          variables: {
            input: {
              procedureStatusId: procedureStatus.id,
              type: 'application',
              name: APPLICATION_TMP_DATA_NAME,
              data,
            },
          },
        })
      }
    } catch (err) {
      if (err instanceof ServerValidationError) {
        eachValidationErrorsForHookFrom(err.validationErrors, schema, data, (path, error) => {
          setError(path as Parameters<typeof setError>[0], error)
        })
        window.scrollTo({top: 0, behavior: 'smooth'})
        return
      }
      dispatch(asyncError(err))
      return
    }

    return await afterSubmit()
  }

  const [updateChildcareInfoFormDatum] = useMutation<unknown, UpdateChildcareInfoFormDatumQueryVariables>(
    UPDATE_CHILDCARE_INFO_FORM_DATUM
  )
  const [updateChildcareInfoFormTmpData] = useMutation<unknown, UpdateChildcareInfoFormTmpDataQueryVariables>(
    UPDATE_CHILDCARE_INFO_FORM_TMP_DATA
  )

  const handleSubmitError = () => {
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  return (
    <FormProvider {...methods}>
      <div className="basic_information l-wrap-xs">
        <FormErrors errors={errors} isSubmitting={isSubmitting} schema={schema} prependFullPath />
        {comments}

        {mode === 'new' && (
          <Panel>
            <Head>養育する子の選択</Head>
            <Body className={styles.formBody}>
              <RadioContainer className={styles.dependents}>
                {childDependents.map((dependent) => (
                  <Radio {...register('childcareDatum.employeeDependentId')} key={dependent.id} value={dependent.id}>
                    {fullNameFormat(dependent.lastName, dependent.firstName)}
                  </Radio>
                ))}
              </RadioContainer>
              <div>
                <Button onClick={handleCopyFromEmployeeDependent} disabled={childDependents.length === 0}>
                  従業員情報から反映する
                </Button>
              </div>
            </Body>
          </Panel>
        )}

        {mode === 'from_past' && (
          <Panel>
            <Head>過去の手続きを選択</Head>
            <Body className={styles.formBody}>
              {note && <Note>{note}</Note>}
              <Select
                options={pastOptions}
                value={pastOptions.find((option) => option.value === currentPastId)}
                onChange={(option) => setSelectedPastId(option?.value)}
                placeholder="選択してください"
              />
              <div>
                <Button onClick={handleReflectFromPast} disabled={!currentPastId}>
                  手続きから情報を反映する
                </Button>
              </div>
            </Body>
          </Panel>
        )}

        <Panel className="u-mt20">
          <Head>養育する子の情報</Head>
          <Body className={styles.formBody}>
            <BoxDouble>
              <div className={styles.field}>
                <Label>姓</Label>
                <Input {...register('childcareChildDatum.lastName')} isError={!!errors.childcareChildDatum?.lastName} />
                {errors.childcareChildDatum?.lastName?.message && (
                  <ErrorMessage>{errors.childcareChildDatum.lastName.message}</ErrorMessage>
                )}
              </div>
              <div className={styles.field}>
                <Label>名</Label>
                <Input
                  {...register('childcareChildDatum.firstName')}
                  isError={!!errors.childcareChildDatum?.firstName}
                />
                {errors.childcareChildDatum?.firstName?.message && (
                  <ErrorMessage>{errors.childcareChildDatum.firstName.message}</ErrorMessage>
                )}
              </div>
            </BoxDouble>
            <BoxDouble>
              <div className={styles.field}>
                <Label>姓（カナ）</Label>
                <Input
                  {...register('childcareChildDatum.lastNameKana')}
                  isError={!!errors.childcareChildDatum?.lastNameKana}
                />
                {errors.childcareChildDatum?.lastNameKana?.message && (
                  <ErrorMessage>{errors.childcareChildDatum.lastNameKana.message}</ErrorMessage>
                )}
              </div>
              <div className={styles.field}>
                <Label>名（カナ）</Label>
                <Input
                  {...register('childcareChildDatum.firstNameKana')}
                  isError={!!errors.childcareChildDatum?.firstNameKana}
                />
                {errors.childcareChildDatum?.firstNameKana?.message && (
                  <ErrorMessage>{errors.childcareChildDatum.firstNameKana.message}</ErrorMessage>
                )}
              </div>
            </BoxDouble>
            <DateField
              name="childcareChildDatum.birthDate"
              label="生年月日（出産年月日）"
              required
              error={errors.childcareChildDatum?.birthDate?.message}
            />
            <div>
              <Checkbox {...register('childcareChildDatum.addSupport')}>社会保険の被扶養者に追加する</Checkbox>
            </div>
          </Body>
        </Panel>

        <Panel className="u-mt20">
          <Head>育児休業期間の情報</Head>
          <Body className={styles.formBody}>
            <div className={styles.field}>
              <Label>区分</Label>
              <RadioContainer>
                <Radio {...register('childcareChildDatum.relation')} value="real">
                  実子
                </Radio>
                <Radio {...register('childcareChildDatum.relation')} value="other">
                  その他
                </Radio>
              </RadioContainer>
            </div>
            <DateField
              name="childcareChildDatum.parentingStartedDate"
              label="養育開始年月日（実子以外）"
              disabled={childRelation !== 'other'}
              error={errors.childcareChildDatum?.parentingStartedDate?.message}
            />
            <DateField
              name="childcareDatum.startedOn"
              label="育児休業等開始年月日"
              required
              error={errors.childcareDatum?.startedOn?.message}
            />
            <DateField
              name="childcareDatum.expectedFinishedOn"
              label="育児休業等終了（予定）年月日"
              error={errors.childcareDatum?.expectedFinishedOn?.message}
            />
            <DateField
              name="childcareDatum.expectedBirthOn"
              label="出産予定日"
              error={errors.childcareDatum?.expectedBirthOn?.message}
            />
            {canAddToLeaveOfAbsence &&
              (leaveOfAbsenceReflected ? (
                <p>すでに休職情報に追加されています</p>
              ) : (
                <Checkbox {...register('childcareDatum.addToLeaveOfAbsence')}>休職情報に追加する</Checkbox>
              ))}
          </Body>
        </Panel>

        <div className="u-ta-c u-mt50">
          {renderReject?.({isSubmitting})}
          <Button primary onClick={handleSubmit(onSubmit, handleSubmitError)} disabled={isSubmitting}>
            {submitText}
          </Button>
        </div>
      </div>
    </FormProvider>
  )
}
